import { useState, useEffect, useCallback } from 'react';
import { notificationTemplateService, NotifyTemplate, CreateNotifyTemplateDto, UpdateNotifyTemplateDto, QueryNotifyTemplateDto } from '../services/notification-template.service';

interface TemplateStats {
  total: number;
  active: number;
  inactive: number;
}

interface UseNotificationTemplatesReturn {
  templates: NotifyTemplate[];
  loading: boolean;
  error: Error | null;
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats: TemplateStats;
  filters: QueryNotifyTemplateDto;
  setFilters: (filters: QueryNotifyTemplateDto) => void;
  refetch: () => void;
  createTemplate: (data: CreateNotifyTemplateDto) => Promise<NotifyTemplate>;
  updateTemplate: (id: string, data: UpdateNotifyTemplateDto) => Promise<NotifyTemplate>;
  deleteTemplate: (id: string) => Promise<void>;
  deleteManyTemplates: (ids: string[]) => Promise<void>;
  cloneTemplate: (id: string) => Promise<NotifyTemplate>;
}

export const useNotificationTemplates = (initialFilters: QueryNotifyTemplateDto = {}): UseNotificationTemplatesReturn => {
  const [templates, setTemplates] = useState<NotifyTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<QueryNotifyTemplateDto>({
    page: 1,
    limit: 20,
    ...initialFilters,
  });
  const [meta, setMeta] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [stats, setStats] = useState<TemplateStats>({ total: 0, active: 0, inactive: 0 });

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [response, statsData] = await Promise.all([
        notificationTemplateService.getNotificationTemplates(filters),
        notificationTemplateService.getNotificationTemplateStats(),
      ]);
      setTemplates(response.data);
      setMeta(response.meta);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch notification templates'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const createTemplate = async (data: CreateNotifyTemplateDto): Promise<NotifyTemplate> => {
    const template = await notificationTemplateService.createNotificationTemplate(data);
    await fetchTemplates();
    return template;
  };

  const updateTemplate = async (id: string, data: UpdateNotifyTemplateDto): Promise<NotifyTemplate> => {
    const template = await notificationTemplateService.updateNotificationTemplate(id, data);
    setTemplates((prev) =>
      prev.map((t) => (t._id === id ? { ...t, ...template } : t))
    );
    return template;
  };

  const deleteTemplate = async (id: string): Promise<void> => {
    await notificationTemplateService.deleteNotificationTemplate(id);
    setTemplates((prev) => prev.filter((t) => t._id !== id));
    setMeta((prev) => ({ ...prev, total: prev.total - 1 }));
  };

  const deleteManyTemplates = async (ids: string[]): Promise<void> => {
    await notificationTemplateService.deleteManyNotificationTemplates(ids);
    setTemplates((prev) => prev.filter((t) => !ids.includes(t._id)));
    setMeta((prev) => ({ ...prev, total: prev.total - ids.length }));
  };

  const cloneTemplate = async (id: string): Promise<NotifyTemplate> => {
    const template = await notificationTemplateService.cloneNotificationTemplate(id);
    await fetchTemplates();
    return template;
  };

  return {
    templates,
    loading,
    error,
    meta,
    stats,
    filters,
    setFilters,
    refetch: fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    deleteManyTemplates,
    cloneTemplate,
  };
};

interface UseNotificationTemplateDetailReturn {
  template: NotifyTemplate | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  updateTemplate: (data: UpdateNotifyTemplateDto) => Promise<NotifyTemplate>;
}

export const useNotificationTemplateDetail = (id: string): UseNotificationTemplateDetailReturn => {
  const [template, setTemplate] = useState<NotifyTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchTemplate = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await notificationTemplateService.getNotificationTemplateById(id);
      setTemplate(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch notification template'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  const updateTemplate = async (data: UpdateNotifyTemplateDto): Promise<NotifyTemplate> => {
    const updated = await notificationTemplateService.updateNotificationTemplate(id, data);
    setTemplate(updated);
    return updated;
  };

  return {
    template,
    loading,
    error,
    refetch: fetchTemplate,
    updateTemplate,
  };
};